import React, { useMemo, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { GROWTH_LEADS_META } from '../../growth-leads/config';
import {
  LD_CAMPAIGNS,
  LD_LEADS,
  LD_SOURCE,
  LD_STATUS,
  LD_STATUS_FILTERS,
} from '../../growth-leads/mockData';
import type { LdLead, LdMetrics, LdSourceKey, LdStatusKey, LdToneKey } from '../../growth-leads/types';
import './growth-leads.css';

const LD_TONE: Record<LdToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const ldEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const TABLE_COLS = '34px 1.4fr 1.1fr 1.6fr 110px 110px 70px 30px';

const BOOKED_STATUSES: LdStatusKey[] = ['booked', 'showed', 'treated'];
const TREATED_STATUSES: LdStatusKey[] = ['showed', 'treated'];

function LdCard({
  children,
  className = '',
  padding = true,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-[22px]' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function LdPill({ status }: { status: LdStatusKey }) {
  const st = LD_STATUS[status];
  const tone = LD_TONE[st.tone];
  return (
    <span
      className="inline-flex items-center gap-[5px] whitespace-nowrap rounded-[var(--r-pill)] px-2.5 py-[3px] text-[11px] font-bold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.accent }} />
      {st.label}
    </span>
  );
}

function LdFunnel({
  label,
  value,
  total,
  tone,
}: {
  label: string;
  value: number;
  total: number;
  tone: LdToneKey;
}) {
  const t = LD_TONE[tone];
  const pct = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <span className="w-[78px] shrink-0 text-right text-xs text-[var(--ink-2)]">{label}</span>
      <div className="h-6 flex-1 overflow-hidden rounded-md bg-[var(--surface-mute)]">
        <div
          className="flex h-full items-center rounded-md pl-2.5"
          style={{
            width: `${Math.max(pct, 4)}%`,
            background: t.accent,
          }}
        >
          {pct > 14 ? <span className="text-[11px] font-bold text-white">{value}</span> : null}
        </div>
      </div>
      <span className="w-[38px] shrink-0 text-right font-[var(--font-mono)] text-xs font-bold text-[var(--ink-1)]">
        {Math.round(pct)}%
      </span>
    </div>
  );
}

function computeMetrics(leads: LdLead[]) {
  const total = leads.length;
  const booked = leads.filter((l) => BOOKED_STATUSES.includes(l.status)).length;
  const treated = leads.filter((l) => TREATED_STATUSES.includes(l.status)).length;
  const noshow = leads.filter((l) => l.status === 'noshow').length;
  const revenue = leads.reduce((sum, l) => sum + (l.value || 0), 0);
  const calls = leads.filter((l) => l.source === 'call');
  const forms = leads.filter((l) => l.source === 'form');
  const callConv =
    calls.length > 0
      ? Math.round(
          (calls.filter((l) => BOOKED_STATUSES.includes(l.status)).length / calls.length) * 100,
        )
      : 0;
  const formConv =
    forms.length > 0
      ? Math.round(
          (forms.filter((l) => BOOKED_STATUSES.includes(l.status)).length / forms.length) * 100,
        )
      : 0;

  return { total, booked, treated, noshow, revenue, calls: calls.length, forms: forms.length, callConv, formConv };
}

function LeadDetailPanel({ lead, loading }: { lead: LdLead; loading?: boolean }) {
  const src = LD_SOURCE[lead.source];

  const apptEmptyMessage =
    lead.status === 'new'
      ? 'Not yet contacted'
      : lead.status === 'contacted'
        ? 'Waiting for patient to book'
        : 'No appointment scheduled';

  if (loading) {
    return (
      <div className="elva-fade bg-[var(--cream-0)] px-5 py-8 text-center text-[13px] text-[var(--ink-3)]">
        Loading lead details…
      </div>
    );
  }

  return (
    <div className="elva-fade bg-[var(--cream-0)] px-5 py-4 pb-[18px]">
      <div className="ld-detail-grid grid grid-cols-3 gap-3">
        <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[15px]">
          <div className={`${ldEyebrowClass} mb-[11px]`}>How they found you</div>
          {[
            ['Campaign', lead.campaign],
            ['Search term', `"${lead.keyword}"`],
            ['Contact method', src.label],
            ...(lead.duration ? [['Call duration', lead.duration] as [string, string]] : []),
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 py-1">
              <span className="shrink-0 text-[12.5px] text-[var(--ink-3)]">{label}</span>
              <span className="text-right text-[12.5px] font-medium text-[var(--ink-1)]">{value}</span>
            </div>
          ))}
        </div>

        <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[15px]">
          <div className={`${ldEyebrowClass} mb-[11px]`}>Appointment</div>
          {lead.apptDate ? (
            <>
              <div className="flex justify-between gap-3 py-1">
                <span className="text-[12.5px] text-[var(--ink-3)]">Scheduled</span>
                <span className="text-right text-[12.5px] font-medium text-[var(--ink-1)]">
                  {lead.apptDate}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 py-1">
                <span className="text-[12.5px] text-[var(--ink-3)]">Status</span>
                <LdPill status={lead.status} />
              </div>
              {lead.treatment ? (
                <div className="flex justify-between gap-3 py-1">
                  <span className="shrink-0 text-[12.5px] text-[var(--ink-3)]">Treatment</span>
                  <span className="text-right text-[12.5px] font-medium text-[var(--ink-1)]">
                    {lead.treatment}
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-[12.5px] italic text-[var(--ink-4)]">{apptEmptyMessage}</div>
          )}
        </div>

        <div
          className="rounded-[var(--r-md)] p-[15px]"
          style={{
            background: lead.value ? 'var(--success-tint)' : 'var(--cream-1)',
            border: lead.value
              ? '1px solid color-mix(in srgb, var(--success) 24%, transparent)'
              : '1px solid var(--cream-2)',
          }}
        >
          <div className={`${ldEyebrowClass} mb-[11px]`}>Revenue</div>
          {lead.value ? (
            <>
              <div className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.03em] text-[var(--success)]">
                ${lead.value}
              </div>
              <div className="mt-[5px] text-xs text-[var(--ink-2)]">from completed treatment</div>
            </>
          ) : lead.status === 'booked' ? (
            <>
              <div className="font-[var(--font-display)] text-lg font-bold text-[var(--gold)]">
                Pending
              </div>
              <div className="mt-[5px] text-xs text-[var(--ink-4)]">Tracked after appointment</div>
            </>
          ) : (
            <div className="text-[12.5px] italic text-[var(--ink-4)]">
              {lead.status === 'noshow'
                ? "No revenue — patient didn't show"
                : 'Not yet converted'}
            </div>
          )}
        </div>
      </div>

      {lead.source === 'call' && (lead.callSummary || lead.duration || lead.recordingUrl) ? (
        <div className="mt-3 flex flex-col gap-3">
          {lead.callSummary ? (
            <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[15px]">
              <div className={`${ldEyebrowClass} mb-2`}>AI call summary</div>
              <p className="m-0 text-[13px] leading-[1.55] text-[var(--ink-1)]">{lead.callSummary}</p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {lead.recordingUrl ? (
              <a
                href={lead.recordingUrl}
                target="_blank"
                rel="noreferrer"
                className="ld-act inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-[7px] font-[var(--font-body)] text-xs font-semibold text-[var(--ink-1)] no-underline"
              >
                <Icon kind="play" size={12} stroke={2} />
                Listen to call recording
              </a>
            ) : lead.duration ? (
              <button
                type="button"
                className="ld-act inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-[7px] font-[var(--font-body)] text-xs font-semibold text-[var(--ink-1)]"
              >
                <Icon kind="play" size={12} stroke={2} />
                Listen to call recording
              </button>
            ) : null}
            {!lead.callSummary && lead.duration ? (
              <button
                type="button"
                className="ld-act inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-[7px] font-[var(--font-body)] text-xs font-semibold text-[var(--ink-1)]"
              >
                <Icon kind="sparkle" size={12} stroke={2} className="text-[var(--ai-3)]" />
                View AI call summary
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {lead.timeline?.length ? (
        <div className="mt-3 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[15px]">
          <div className={`${ldEyebrowClass} mb-2.5`}>Timeline</div>
          <div className="flex flex-col gap-2">
            {lead.timeline.map((event, index) => (
              <div key={`${event.at}-${index}`} className="flex justify-between gap-3 py-0.5">
                <span className="text-[12.5px] font-medium text-[var(--ink-1)]">{event.label}</span>
                <span className="shrink-0 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
                  {event.at}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LeadRow({
  lead,
  detailLead,
  detailLoading,
  open,
  onToggle,
  last,
}: {
  lead: LdLead;
  detailLead?: LdLead;
  detailLoading?: boolean;
  open: boolean;
  onToggle: () => void;
  last: boolean;
}) {
  const src = LD_SOURCE[lead.source];
  const srcTone = LD_TONE[src.tone];

  return (
    <div style={{ borderBottom: last && !open ? undefined : '1px solid var(--cream-2)' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="ld-row ld-table-row grid cursor-pointer items-center gap-3 px-5 py-[13px] transition-[background] duration-[var(--dur-fast)]"
        style={{
          gridTemplateColumns: TABLE_COLS,
          background: open ? 'var(--ai-tint)' : undefined,
        }}
      >
        <span
          title={src.label}
          className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg"
          style={{ background: srcTone.tint, color: srcTone.accent }}
        >
          <Icon kind={src.icon} size={14} stroke={1.8} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-[var(--ink-0)]">{lead.name}</div>
          <div className="mt-px font-[var(--font-mono)] text-[11.5px] text-[var(--ink-4)]">
            {lead.phone}
          </div>
        </div>
        <span className="ld-col-campaign max-w-full justify-self-start truncate whitespace-nowrap rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-[9px] py-[3px] text-[11.5px] font-semibold text-[var(--ink-2)]">
          {lead.campaign}
        </span>
        <span className="ld-col-keyword truncate text-xs italic text-[var(--ink-3)]">
          &ldquo;{lead.keyword}&rdquo;
        </span>
        <span className="ld-col-date whitespace-nowrap font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
          {lead.date}
        </span>
        <LdPill status={lead.status} />
        <span className="ld-col-revenue text-right">
          {lead.value ? (
            <span className="font-[var(--font-display)] text-[15px] font-bold text-[var(--success)]">
              ${lead.value}
            </span>
          ) : lead.status === 'booked' ? (
            <span className="text-[11px] font-semibold text-[var(--gold)]">Pending</span>
          ) : (
            <span className="text-xs text-[var(--ink-4)]">—</span>
          )}
        </span>
        <Icon
          kind="down"
          size={15}
          stroke={2}
          className="justify-self-end text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </div>
      {open ? (
        <LeadDetailPanel
          lead={detailLead ?? lead}
          {...(detailLoading ? { loading: true } : {})}
        />
      ) : null}
    </div>
  );
}

export interface PatientLeadsViewProps {
  items?: LdLead[];
  metrics?: LdMetrics;
  campaignOptions?: string[];
  loading?: boolean;
  onStatusChange?: (leadId: number, status: LdStatusKey) => void;
  onLeadExpand?: (leadId: number | null) => void;
  leadDetails?: Record<number, LdLead>;
  leadDetailLoadingId?: number | null;
}

export function PatientLeadsView({
  items: itemsProp,
  metrics: metricsProp,
  campaignOptions,
  loading = false,
  onStatusChange: _onStatusChange,
  onLeadExpand,
  leadDetails,
  leadDetailLoadingId = null,
}: PatientLeadsViewProps = {}) {
  const meta = GROWTH_LEADS_META;
  const sourceLeads = itemsProp ?? LD_LEADS;
  const campaigns = campaignOptions ?? LD_CAMPAIGNS;
  const [campaignFilter, setCampaignFilter] = useState('All campaigns');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [openId, setOpenId] = useState<number | null>(null);

  const metrics = useMemo(
    () => metricsProp ?? computeMetrics(sourceLeads),
    [metricsProp, sourceLeads],
  );

  const filtered = useMemo(
    () =>
      sourceLeads.filter(
        (lead) =>
          (campaignFilter === 'All campaigns' || lead.campaign === campaignFilter) &&
          (statusFilter === 'All' || lead.status === statusFilter),
      ),
    [campaignFilter, statusFilter, sourceLeads],
  );

  const kpis = [
    { label: 'Total leads', value: String(metrics.total), tone: null as LdToneKey | null },
    { label: 'Booked', value: String(metrics.booked), tone: 'ai' as const },
    { label: 'Treated', value: String(metrics.treated), tone: 'success' as const },
    { label: 'No-shows', value: String(metrics.noshow), tone: 'terra' as const },
    {
      label: 'Revenue generated',
      value: `$${(metrics.revenue / 1000).toFixed(1)}k`,
      tone: 'success' as const,
    },
    {
      label: 'Booking rate',
      value: metrics.total > 0 ? `${Math.round((metrics.booked / metrics.total) * 100)}%` : '0%',
      tone: null as LdToneKey | null,
    },
  ];

  return (
    <div className="growth-leads-root relative mx-auto w-full max-w-[1080px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="team" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <div
        className="ld-kpi-grid mb-4 grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
      >
        {kpis.map((kpi) => (
          <LdCard key={kpi.label} className="px-4 py-3.5">
            <div className={ldEyebrowClass}>{kpi.label}</div>
            <div
              className="mt-2 font-[var(--font-display)] text-[22px] font-extrabold leading-none tracking-[-0.04em]"
              style={{ color: kpi.tone ? LD_TONE[kpi.tone].accent : 'var(--ink-0)' }}
            >
              {kpi.value}
            </div>
          </LdCard>
        ))}
      </div>

      <div
        className="ld-funnel-grid mb-7 grid gap-4"
        style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <LdCard>
          <div className="mb-4 font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Lead → patient funnel
          </div>
          <div className="flex flex-col gap-[9px]">
            <LdFunnel label="Leads" value={metrics.total} total={metrics.total} tone="neutral" />
            <LdFunnel label="Booked" value={metrics.booked} total={metrics.total} tone="ai" />
            <LdFunnel label="Showed up" value={metrics.treated} total={metrics.total} tone="success" />
            <LdFunnel label="No-show" value={metrics.noshow} total={metrics.total} tone="terra" />
          </div>
        </LdCard>

        <LdCard>
          <div className="mb-4 font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Lead sources
          </div>
          <div className="mb-3.5 flex gap-3">
            {(['call', 'form'] as LdSourceKey[]).map((sourceKey) => {
              const src = LD_SOURCE[sourceKey];
              const tone = LD_TONE[src.tone];
              const count = sourceKey === 'call' ? metrics.calls : metrics.forms;
              return (
                <div
                  key={sourceKey}
                  className="flex-1 rounded-[var(--r-md)] border p-4 text-center"
                  style={{
                    background: tone.tint,
                    borderColor: `color-mix(in srgb, ${tone.accent} 24%, transparent)`,
                  }}
                >
                  <span className="mx-auto mb-2 flex h-[34px] w-[34px] items-center justify-center rounded-[9px] bg-[var(--cream-1)] text-[var(--success)]">
                    <Icon kind={src.icon} size={17} stroke={1.9} style={{ color: tone.accent }} />
                  </span>
                  <div
                    className="font-[var(--font-display)] text-2xl font-extrabold leading-none tracking-[-0.03em]"
                    style={{ color: tone.accent }}
                  >
                    {count}
                  </div>
                  <div className="mt-1 text-xs font-medium text-[var(--ink-2)]">
                    {sourceKey === 'call' ? 'Phone calls' : 'Form submissions'}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[var(--ink-4)]">
                    {Math.round((count / metrics.total) * 100)}% of leads
                  </div>
                </div>
              );
            })}
          </div>
          <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-[11px] text-xs leading-[1.5] text-[var(--ink-2)]">
            Phone calls convert at{' '}
            <b className="font-semibold text-[var(--success)]">{metrics.callConv}%</b> vs. forms at{' '}
            <b className="font-semibold text-[var(--info)]">{metrics.formConv}%</b> booking rate.
          </div>
        </LdCard>
      </div>

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="ld-sel"
          >
            {campaigns.map((campaign) => (
              <option key={campaign} value={campaign}>
                {campaign}
              </option>
            ))}
          </select>
          <div className="inline-flex flex-wrap gap-[3px] rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 shadow-[var(--sh-1)]">
            {LD_STATUS_FILTERS.map((status) => {
              const active = statusFilter === status;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  className={[
                    'cursor-pointer whitespace-nowrap rounded-[var(--r-pill)] border-0 px-3 py-1.5 font-[var(--font-body)] text-[11.5px]',
                    active
                      ? 'bg-[var(--surface-mute)] font-semibold text-[var(--ink-0)]'
                      : 'bg-transparent font-medium text-[var(--ink-3)]',
                  ].join(' ')}
                >
                  {status === 'All' ? 'All' : LD_STATUS[status as LdStatusKey].label}
                </button>
              );
            })}
          </div>
        </div>
        <span className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">
          {filtered.length} leads · {meta.periodLabel}
          {loading ? ' · Updating…' : ''}
        </span>
      </div>

      <LdCard className="overflow-hidden !p-0">
        <div
          className="ld-table-head grid items-center gap-3 border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--ink-3)]"
          style={{ gridTemplateColumns: TABLE_COLS }}
        >
          <span />
          <span>Patient</span>
          <span className="ld-col-campaign">Campaign</span>
          <span className="ld-col-keyword">Search term</span>
          <span className="ld-col-date">Date</span>
          <span>Status</span>
          <span className="ld-col-revenue text-right">Revenue</span>
          <span />
        </div>
        {filtered.length === 0 ? (
          <div className="px-5 py-11 text-center text-[13px] text-[var(--ink-3)]">
            No leads match your filters.
          </div>
        ) : (
          filtered.map((lead, index) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              {...(leadDetails?.[lead.id] ? { detailLead: leadDetails[lead.id] } : {})}
              {...(leadDetailLoadingId === lead.id ? { detailLoading: true } : {})}
              open={openId === lead.id}
              onToggle={() => {
                const next = openId === lead.id ? null : lead.id;
                setOpenId(next);
                onLeadExpand?.(next);
              }}
              last={index === filtered.length - 1}
            />
          ))
        )}
      </LdCard>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="text-xs text-[var(--ink-4)]">{meta.footerNote}</span>
        <span className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">
          {sourceLeads.length} total leads this month
        </span>
      </div>
    </div>
  );
}
